import { Body, Controller, Get, HttpCode, HttpStatus, Param, Patch, Post, Query, Req, UseGuards } from '@nestjs/common';
import { IsIn } from 'class-validator';
import { AgentJwtGuard, AgentScopedRequest } from '../common/guards/agent-jwt.guard';
import { ChatService } from './chat.service';
import { AgentReplyDto } from './dto/agent-reply.dto';
import {
  AddServiceRequestNoteDto,
  AssignServiceRequestDto,
  ListServiceRequestsQueryDto,
  TransferServiceRequestDto,
  UpdateServiceRequestStatusDto,
} from './dto/service-request.dto';
import { AgentAvailability, PresenceService } from './presence.service';
import { ServiceRequestService } from './service-request.service';

class AgentStatusDto {
  @IsIn(['online', 'busy'])
  status!: AgentAvailability;
}

// The human side of an escalated conversation. Where ChatController speaks
// "application API key" (the embedding product), this controller speaks
// "admin login JWT" (a person) — AgentJwtGuard resolves which application(s)
// that person may answer for, and every handler simply inherits that scope.
//
// Every authenticated call doubles as a presence heartbeat: the inbox polls
// while open, so "an agent is online" falls straight out of the traffic.
@UseGuards(AgentJwtGuard)
@Controller('agent')
export class AgentController {
  constructor(
    private readonly chatService: ChatService,
    private readonly presence: PresenceService,
    private readonly serviceRequests: ServiceRequestService,
  ) {}

  // ── Service Requests ────────────────────────────────────────────────────

  @Get('service-requests')
  listServiceRequests(@Req() request: AgentScopedRequest, @Query() query: ListServiceRequestsQueryDto) {
    this.heartbeat(request);
    return this.serviceRequests.list(request.agent.applicationId, request.agent.userId, query);
  }

  @Get('service-requests/:id')
  getServiceRequest(@Req() request: AgentScopedRequest, @Param('id') id: string) {
    this.heartbeat(request);
    return this.serviceRequests.getForAgentOrThrow(request.agent.applicationId, id);
  }

  @Patch('service-requests/:id/status')
  updateStatus(
    @Req() request: AgentScopedRequest,
    @Param('id') id: string,
    @Body() dto: UpdateServiceRequestStatusDto,
  ) {
    this.heartbeat(request);
    const actor = { id: request.agent.userId, name: request.agent.name };

    if (dto.status === 'open') {
      // "Reopen" is the only valid way back to open from closed — routed to
      // its own method so it logs a distinct timeline event type.
      return this.serviceRequests.reopen(request.agent.applicationId, id, actor);
    }

    return this.serviceRequests.updateStatus(request.agent.applicationId, id, dto.status, actor);
  }

  @Post('service-requests/:id/assign')
  assign(@Req() request: AgentScopedRequest, @Param('id') id: string, @Body() dto: AssignServiceRequestDto) {
    this.heartbeat(request);
    const agentId = dto.agentId ?? request.agent.userId;
    const agentName = dto.agentName ?? request.agent.name;
    const actor = { id: request.agent.userId, name: request.agent.name };

    return this.serviceRequests.assign(request.agent.applicationId, id, agentId, agentName, actor);
  }

  @Post('service-requests/:id/transfer')
  transfer(@Req() request: AgentScopedRequest, @Param('id') id: string, @Body() dto: TransferServiceRequestDto) {
    this.heartbeat(request);
    const actor = { id: request.agent.userId, name: request.agent.name };

    return this.serviceRequests.transfer(request.agent.applicationId, id, dto.agentId, dto.agentName, actor);
  }

  @Get('service-requests/:id/notes')
  listNotes(@Req() request: AgentScopedRequest, @Param('id') id: string) {
    this.heartbeat(request);
    return this.serviceRequests.listNotes(request.agent.applicationId, id);
  }

  @Post('service-requests/:id/notes')
  addNote(@Req() request: AgentScopedRequest, @Param('id') id: string, @Body() dto: AddServiceRequestNoteDto) {
    this.heartbeat(request);
    const actor = { id: request.agent.userId, name: request.agent.name };

    return this.serviceRequests.addNote(request.agent.applicationId, id, dto.content, actor);
  }

  @Get('service-requests/:id/timeline')
  timeline(@Req() request: AgentScopedRequest, @Param('id') id: string) {
    this.heartbeat(request);
    return this.serviceRequests.timeline(request.agent.applicationId, id);
  }

  @Get('service-requests/:id/messages')
  async serviceRequestMessages(@Req() request: AgentScopedRequest, @Param('id') id: string) {
    this.heartbeat(request);
    const serviceRequest = await this.serviceRequests.getForAgentOrThrow(request.agent.applicationId, id);
    return this.chatService.listMessagesForAgent(request.agent.applicationId, serviceRequest.sessionId);
  }

  @Post('service-requests/:id/messages')
  async serviceRequestReply(@Req() request: AgentScopedRequest, @Param('id') id: string, @Body() dto: AgentReplyDto) {
    this.heartbeat(request);
    const serviceRequest = await this.serviceRequests.getForAgentOrThrow(request.agent.applicationId, id);
    return this.chatService.agentReply(request.agent.applicationId, serviceRequest.sessionId, dto.content, request.agent.name);
  }

  // ── Legacy session-centric endpoints (kept for chat-widget-ui's existing
  // agent-mode iframe path — unused by the native admin Support Center) ────

  @Get('sessions')
  listSessions(@Req() request: AgentScopedRequest) {
    this.heartbeat(request);
    return this.chatService.listSessionsForAgent(request.agent.applicationId);
  }

  @Get('sessions/:sessionId/messages')
  listMessages(@Req() request: AgentScopedRequest, @Param('sessionId') sessionId: string) {
    this.heartbeat(request);
    return this.chatService.listMessagesForAgent(request.agent.applicationId, sessionId);
  }

  @Post('sessions/:sessionId/messages')
  reply(
    @Req() request: AgentScopedRequest,
    @Param('sessionId') sessionId: string,
    @Body() dto: AgentReplyDto,
  ) {
    this.heartbeat(request);
    return this.chatService.agentReply(request.agent.applicationId, sessionId, dto.content, request.agent.name);
  }

  // Manual availability — Busy can't be derived from traffic, only declared.
  @Post('status')
  @HttpCode(HttpStatus.NO_CONTENT)
  setStatus(@Req() request: AgentScopedRequest, @Body() dto: AgentStatusDto): void {
    this.presence.setAgentStatus(request.agent.applicationId, request.agent.userId, dto.status, request.agent.name);
  }

  @Post('sessions/:sessionId/typing')
  @HttpCode(HttpStatus.NO_CONTENT)
  async typing(@Req() request: AgentScopedRequest, @Param('sessionId') sessionId: string): Promise<void> {
    this.heartbeat(request);
    // Scope check first — an agent must not be able to set flags on another
    // store's session, same rule as reading it.
    await this.chatService.assertAgentSession(request.agent.applicationId, sessionId);
    this.presence.markTyping(sessionId, 'agent', request.agent.name);
  }

  // Transient per-session state the transcript poll wants alongside messages.
  @Get('sessions/:sessionId/live')
  async live(@Req() request: AgentScopedRequest, @Param('sessionId') sessionId: string) {
    this.heartbeat(request);
    await this.chatService.assertAgentSession(request.agent.applicationId, sessionId);
    return { shopperTyping: this.presence.isTyping(sessionId, 'shopper') };
  }

  private heartbeat(request: AgentScopedRequest): void {
    this.presence.markAgentActive(request.agent.applicationId, request.agent.userId, request.agent.name);
  }
}
